"use client";

import React, { useEffect, useState } from "react";
import { Plus } from "geist-icons";
import Link from "next/link";
import LaserFlow from "./laser-flow";
import { NextjsIcon } from "../about/NextjsIcon";
import { useTheme } from "next-themes";
import GridBeams from "./GridBeams";
import { useMediaQuery } from "@/lib/hooks/use-media-query";

function useIsSmall() {
  const [isSmall, setIsSmall] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 639px)");
    setIsSmall(mq.matches);
    const handler = (e: MediaQueryListEvent) => setIsSmall(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  return isSmall;
}

export default function NextjsHome() {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const isMedium = useMediaQuery("(min-width: 768px)");
  const isSmall = useIsSmall();

  return (
    <section className="w-full bg-[#FAFAFA] dark:bg-black min-w-[320px] flex items-center justify-center px-4 sm:px-6 md:px-8 lg:px-10" id="nextjs">
      {/* Wrapper to control the width of the grid */}
      <div className="relative z-[20] w-full max-w-[1100px]">
        {/* Grid container with fixed height classes */}
        <div className="relative z-[50] w-full h-full sm:h-[550px] md:h-[600px] lg:h-[700px] border-l-2 border-r-2 border-[#EAEAEA] dark:border-[#1E1E1E] flex flex-col mx-auto items-center justify-center">
          {/* Top right plus sign */}
          <div className="absolute z-[30] md:-top-[11px] -top-[11px] md:-right-[11px] -right-[11px] flex items-center justify-center">
            <Plus className="scale-100 md:scale-130 text-[#A8A8A8] dark:text-[#878787]" />
          </div>

          {/* Vertical grid lines */}
          <div className="relative w-full h-full items-center justify-center">
            <div className="relative flex flex-col h-full items-center justify-start">
              <div className="flex flex-col w-full items-start sm:mb-0 px-2">
                <div className="flex flex-row ml-3.5 md:ml-5 z-[30]">
                  <h1 className="lg:text-6xl md:text-5xl text-4xl mt-9 md:mt-6 lg:mt-7.5 mr-5 font-bold">
                    Next.js
                  </h1>
                  <h4 className="lg:text-4xl md:text-2xl text-xl mt-12 md:mt-10 lg:mt-12 font-bold">
                    for an excellent framework
                  </h4>
                </div>
                <div className="flex z-[30] flex-row md:gap-10 gap-5 mt-5 ml-3.5 md:ml-5">
                  <Link href="/tools" className="underline">
                    Look at my tools
                  </Link>
                </div>
              </div>
              <div className="flex w-full flex-col sm:flex-row h-[500px] sm:h-2/3 items-center justify-center">
                <div className="flex flex-col sm:flex-row w-full sm:w-2/5 h-[20%] sm:h-full items-center justify-end sm:justify-center">
                  <div className="relative flex flex-col sm:flex-row sm:absolute items-center w-full sm:w-[58%] sm:h-1/3 sm:left-0 scale-77 sm:scale-160">
                    <NextjsIcon className="flex absolute z-10 sm:left-[28%] top-2.5 sm:top-[43%] md:top-[45%] scale-280 sm:scale-200 md:scale-230 lg:scale-280" />
                    <LaserFlow
                      color={isDark ? "#FFFFFF" : "#000000"}
                      style={{
                        transform: isSmall ? "rotate(0deg)" : "rotate(-90deg)",
                      }}
                      horizontalBeamOffset={0}
                      verticalBeamOffset={isMedium ? -0.39 : -0.32}
                      dpr={2}
                      wispDensity={2}
                      flowSpeed={0.5}
                      horizontalSizing={0.5}
                      verticalSizing={1}
                      flowStrength={0.5}
                      fogFallSpeed={0}
                      fogScale={0}
                      fogIntensity={0}
                      falloffStart={0.5}
                    />
                  </div>
                </div>
                <div className="flex w-4/5 sm:w-3/5 h-[15%] sm:h-full items-center justify-center sm:justify-start">
                  <div className="relative w-[90%] min-h-[200px] overflow-hidden">
                    {/* Tab Page  */}
                    <div className="relative group bg-white dark:bg-black rounded-xl w-full h-full mx-auto mt-3 md:mt-4 border border-[#C5C5C5] dark:border-[#1E1E1E] space-y-4">
                      {/* Address bar */}
                      <div className="flex items-center rounded-xl rounded-b-none bg-[#F0F0F0] dark:bg-[#0A0A0A] p-2 pl-4 gap-4 lg:gap-8 py-2">
                        <div className="flex flex-row gap-1.5">
                          <div className="w-2 h-2 rounded-full bg-red-500" />
                          <div className="w-2 h-2 rounded-full bg-yellow-500" />
                          <div className="w-2 h-2 rounded-full bg-green-500" />
                        </div>
                      </div>

                      <div className="relative flex flex-col w-full h-full gap-5">
                        <GridBeams className="relative flex w-full h-full text-[#EAEAEA] dark:text-[#1E1E1E]" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
